import { translateTableHead } from 'src/careflow/helpers/common';
import { TABLE_HEAD_WORKFLOW_INSTANCE_PATIENT } from 'src/careflow/common/table-head';
import useObjectState from 'src/hooks/useObjectState';
import useTable from 'src/hooks/useTable';
import { PaginateQuery } from 'src/api/pagination/dtos';
import { useEffect } from 'react';
import { Button, MenuItem } from '@mui/material';
import { EpisodeOfCareService } from '../service/EpisodeOfCareService';
import useNotiMessage from 'src/hooks/useNotiMessage';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { workflowInstanceMap } from 'src/careflow/common/common-utils';
import { PATH_DASHBOARD } from 'src/routes/paths';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { paramCase } from 'change-case';
import SummaryTable from '../../patient/cases/SummaryTable';
import { Add } from '@mui/icons-material';
import { PatientEpisodeOfCareForm } from './PatientEpisodeOfCareForm';

export default function PatientEpisodeOfCare({ patient, i18n, title }: any) {
  const { configurations } = useTenantConfigData();
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: 5,
    defaultOrderBy: 'id',
    initialIndex: 1,
    defaultCurrentPage: 1,
  });

  const { showErrorMessage } = useNotiMessage();

  const [{ data, openForm, refresh }, updateState] = useObjectState<any>({
    data: null,
    openForm: false,
    refresh: false,
    checklistData: null,
    eocRecord: null,
    statisticsData: null,
  });

  const getPaginateQuery = (
    patientId: string,
    page: number,
    rowsPerPage: number
  ): PaginateQuery => ({
    page,
    limit: rowsPerPage,
    filter: {
      indexedSubjectFhirRefUri: `${configurations.fhir.baseUrl}/Patient/${patientId}`,
    },
  });

  const fetchEpisodeOfCareList = async (patientId: string, page: number, rowsPerPage: number) => {
    try {
      updateState({ isLoading: true });
      const data = await EpisodeOfCareService.getAll(
        getPaginateQuery(patientId, page, rowsPerPage)
      );
      updateState({ data: data });
    } catch (error) {
      showErrorMessage(i18n('errorMessages.error'));
    } finally {
      updateState({ isLoading: false, refresh: false });
    }
  };

  useEffect(() => {
    if (!patient?.id || !configurations) return;

    fetchEpisodeOfCareList(patient.id, page, rowsPerPage);
  }, [patient?.id, page, rowsPerPage, refresh, configurations]);

  useEffect(() => {
    if (!patient?.id || !configurations) return;
    fetchEpisodeOfCareList(patient?.id, page, rowsPerPage);
  }, [page, rowsPerPage, refresh, patient?.id]);

  const dataMapped = data?.data
    ? data?.data?.map((r: any) => workflowInstanceMap(r, PATH_DASHBOARD.crs.episodeOfCare.root))
    : [];

  return (
    <>
      <SummaryTable
        title={title}
        columns={translateTableHead(TABLE_HEAD_WORKFLOW_INSTANCE_PATIENT, i18n, 'crs')}
        data={dataMapped}
        page={page}
        count={data ? data?.meta?.totalItems : 0}
        rowsPerPage={rowsPerPage}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        handleRefresh={() => {}}
        actions={
          checkAclValidation({ acls: [crsAcls.CRS.PATIENT.EPISODE_OF_CARE.ADD] }) ? (
            <Button
              variant="contained"
              size="small"
              sx={{ height: '36px' }}
              startIcon={<Add />}
              onClick={() => updateState({ openForm: true })}
            >
              {i18n('episodeOfCare.buttonAdd', 'crs')}
            </Button>
          ) : null
        }
        menuItems={(row) => [
          checkAclValidation({ acls: [crsAcls.CRS.EPISODE_OF_CARE.VIEW] }) && (
            <a
              key="detail"
              href={PATH_DASHBOARD.crs.episodeOfCare.details(paramCase(row.id))}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <MenuItem sx={{ color: 'black' }}>
                <OpenInNewIcon />
                {i18n('episodeOfCare.detail', 'crs')}
              </MenuItem>
            </a>
          ),
        ]}
      />
      <PatientEpisodeOfCareForm
        open={openForm}
        patient={patient}
        onClose={() => updateState({ openForm: false })}
        episodeOfCare={null}
        refresh={() => updateState({ refresh: true })}
      />
    </>
  );
}
